import { abGetDefaultCity } from '../ab/widgets-home';
import { $api } from '../y/api';
import { $i18n, yGetI18N, YI18NString } from '../y/i18n';
import { $time, YTime } from '../y/time';
import { YCountryId, YId, YTimeString } from '../y/types';
import { c_cities, CCityId } from './c-city';
import { CEventId } from './c-event';
import { c_event_tags, CEventTagId, cEventTagName } from './c-event-tag';
import { CProducerId } from './c-producer';
import { CVenue, CVenueId } from './c-venue';

export interface CEventHomeListViewSession {
    time: YTimeString;
    is_bookable: boolean;
    is_drop_in: boolean;
}

export interface CEventHomeListView {
    id: CEventId;
    name: YI18NString;
    city_id: CCityId;
    venue_id: CVenueId;
    producer_id: CProducerId;
    slug: string;
    venue_name: string;
    event_tag_ids: CEventTagId[];
    sessions: CEventHomeListViewSession[];
    is_loyalty: boolean;
    rating_score?: number;
    picture_id: YId;
    popularity: number;
    dots: number;
    published_at: YTimeString;
    has_videos: boolean;

    //cached
    _search: string;
}

export function cEventHomeListViewSessionTimeAfter(event: CEventHomeListView, when: YTime) {
    let bookable: YTime | undefined;
    for(const s of event.sessions) {
        const t = $time(s.time);
        if(t.after(when))
            return t;
        else if(!bookable)
            bookable = t;
    }
    return bookable;
}

export function cEventHomeListViewNextSessionTime(event: CEventHomeListView, after?: YTime) {
    let bookable = event.sessions.filter(s => s.is_bookable);
    if(bookable.length) {
        if(after) {
            const filtered_bookable = bookable.filter(s => $time(s.time).after(after));
            if(filtered_bookable.length)
                bookable = filtered_bookable;
        }
        if(bookable.length > 1)
            return $i18n.tl.several_dates;
        const text = $time(bookable[0].time).formatIn(bookable[0].is_drop_in ? 'd MMM' : 'd MMM, HH:mm', 'Europe/Stockholm');
        return text;
    }

    // if(model.videos && model.videos.length && this.isVideoAvailable(model.videos[0])) {
    //     const time = $time(model.videos[0].date);
    //     if(time.is_future) {
    //         return $i18n.tl.t_video_starts + ' ' + time.format('d MMM, HH:mm');
    //     } else if(time.add(model.length, $time.minute).is_future) {
    //         return $i18n.tl.t_video_live_now;
    //     }
    // }

    return;
}

export function cEventHomeListViewScreenLocation(event: CEventHomeListView) {
    return ['event', c_cities.maybe(event.city_id)!.slug, event.slug];
}

export function cEventHomeListFilterActive(e: CEventHomeListView) {
    return e.sessions.some(s => s.is_bookable);
}

export function cEventHomeListFilterFuture(e: CEventHomeListView) {
    return e.sessions.length > 0;
}

export function cEventHomeListFilterUpcoming(e: CEventHomeListView) {
    return e.sessions.some(s => s.is_bookable); //TODOURGENT: || has live stream
}

export function cEventHomeListFilterPast(e: CEventHomeListView) {
    return !e.sessions.length;
}

export function cEventHomeListInRange(e: CEventHomeListView, from: YTime, to: YTime) {
    return e.sessions.some(s => $time(s.time).after(from) && $time(s.time).before(to));
}

export function cEventHomeListMatchesQuery(e: CEventHomeListView, query: string[]) {
    if(!query.length) return false;

    if(!e._search) {
        let search = ' ' + yGetI18N(e.name) + ' ' + e.venue_name;
        const now = $time.now();
        const we_start = $time.make(now.year, now.month, now.day_of_month - 1 + (7 - now.day_of_week) % 7);
        const we_end = we_start.add(2, $time.day);
        const today = now.formatIn('EEEE d MMMM', 'Europe/Stockholm');
        const tomorrow = now.add(1, $time.day).formatIn('EEEE d MMMM', 'Europe/Stockholm');
        for(const s of e.sessions) {
            if(!s.is_bookable) continue;
            const time = $time(s.time);
            const date = time.formatIn('EEEE d MMMM', 'Europe/Stockholm');
            search += ' ' + date;
            if(date === today)
                search += ' idag';
            if(date === tomorrow)
                search += ' imorgon';
            if(time.after(we_start) && time.before(we_end))
                search += ' helgen';
        }
        for(const t of e.event_tag_ids) {
            const tag = c_event_tags.maybe(t);
            if(tag)
                search += ' ' + cEventTagName(tag);
        }

        search += ' ';

        e._search = search.toLocaleLowerCase();
    }

    const search = e._search;

    for(const w of query) {
        if(!search.includes(w))
            return false;
    }

    return true;
}

function getHomeListView<Data = CEventHomeListView[]>(sub_path: string) {
    return $api.cachedGET<Data>(`c/events/home_list_view/${sub_path}`);
}

export function cEventsHomeListViewFeaturedByCountry(country: YCountryId) {
    return getHomeListView(`featured/${country}`);
}

export function cEventsHomeListViewActiveByEventTag(event_tag_id: CEventTagId) {
    return getHomeListView(`active/by_event_tag/${event_tag_id}`);
}

export function cEventsHomeListViewActiveByCity(city_id: CCityId = abGetDefaultCity().id) {
    return getHomeListView(`active/by_city/${city_id}`);
}

interface CEventHomeListViewVenueResponse {
    venue: CVenue;
    events: CEventHomeListView[];
}

export function cEventsHomeListViewActiveByVenueSlug(city_id: CCityId, venue_slug: string) {
    return getHomeListView<CEventHomeListViewVenueResponse>(`active/by_venue_slug/${city_id}/${venue_slug}`);
}

interface CEventHomeListViewProducerResponse {
    producer_id: CProducerId;
    producer_name: string;
    events: CEventHomeListView[];
}

export function cEventsHomeListViewActiveByProducerSlug(producer_slug: string) {
    return getHomeListView<CEventHomeListViewProducerResponse>(`active/by_producer_slug/${producer_slug}`);
}

export function cEventsHomeListViewOnline(country: YCountryId) {
    return getHomeListView(`online/${country}`);
}

export function cEventsHomeListViewOnlineByEventTag(country: YCountryId, event_tag_id: CEventTagId) {
    return getHomeListView(`online/${country}/by_event_tag/${event_tag_id}`);
}

export function cEventsHomeListViewPast(city_id: CCityId, days: number) {
    return getHomeListView(`past/${city_id}/${days}`);
}

export function cEventsHomeListViewPastByYear(city_id: CCityId, year: string) {
    return getHomeListView(`past/${city_id}/by_year/${year}`);
}
